import { MULTI_OFFICE_ID } from "@/common/CommonConstants";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { DriverWorkCycleList } from "@/components/driverWorkCycles/DriverWorkCycleList";
import { AutoCompleteMultiple } from "@/components/molecules/AutoCompleteMultiple";
import Title from "@/components/molecules/Title";
import {
  DriverWorkCycleOrder,
  DriverWorkCycleSortKey,
  DriverWorkCyclesState,
  OfficeRelatedShiftAndCycle,
} from "@/models/driverWorkShiftsAndCycle";
import { Office, ONLY_CONTRACT } from "@/models/office";
import { getOfficesAPI } from "@/util/api/API";
import {
  getDriverWorkCyclesPageAPI,
  getDriverWorkCyclesTotalAPI,
} from "@/util/api/driverWorkCycles";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { Grid, Paper } from "@mui/material";
import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";
import {
  DriverWorkCyclesConstants,
  PAGE_SIZE,
} from "./DriverWorkCyclesConstants";
import { getOfficeOptionLabel } from "@/util";

export const DriverWorkCycles = () => {
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<DriverWorkCycleOrder>({
    "offices.displayName": "asc",
  });

  const [driverWorkCycles, setDriverWorkCycles] = useState<
    OfficeRelatedShiftAndCycle[]
  >([]);

  const [offices, setOffices] = useState<Office[]>([]);
  const [officesSelected, setOfficesSelected] = useState<Office[]>([]);

  const [emptyResult, setEmptyResult] = useState(false);

  const { savedState, navigate } =
    useStatefulNavigator<DriverWorkCyclesState>();

  const updateList = useCallback(
    (page: number, order: DriverWorkCycleOrder, officeIds: number[]) => {
      getDriverWorkCyclesPageAPI(page, PAGE_SIZE, order, officeIds).then(
        (res) => setDriverWorkCycles(res.data)
      );

      getDriverWorkCyclesTotalAPI(officeIds).then((res) => {
        setTotal(res.data ?? 0);
        setPage(page);
        setEmptyResult(res.data === 0);
      });
    },
    []
  );

  const handleChangeOffices = useCallback((value: Office[]) => {
    setOfficesSelected(value);
    Cookies.set(MULTI_OFFICE_ID, value.map(({ id }) => id).join(","));
  }, []);

  const handleClickSearch = useCallback(() => {
    updateList(
      1,
      order,
      officesSelected.map(({ id }) => id)
    );
  }, [officesSelected, order, updateList]);

  const handleClickReset = useCallback(() => {
    setOfficesSelected([]);
    setOrder({
      "offices.displayName": "asc",
    });
  }, []);

  const handleChangePage = useCallback(
    (page: number) => {
      updateList(
        page,
        order,
        officesSelected.map(({ id }) => id)
      );
    },
    [officesSelected, order, updateList]
  );

  const handleChangeSort = useCallback(
    (key: DriverWorkCycleSortKey) => {
      const nextDirection = ["asc", undefined].includes(order[key])
        ? "desc"
        : "asc";

      const nextOrder: DriverWorkCycleOrder = {
        [key]: nextDirection,
      };

      updateList(
        1,
        nextOrder,
        officesSelected.map(({ id }) => id)
      );
      setOrder(nextOrder);
    },
    [officesSelected, order, updateList]
  );

  const handleClickEdit = useCallback(
    (officeId: number) => {
      navigate(`/driver_work_cycles/${officeId}`, {
        page,
        order,
        officeIds: officesSelected.map(({ id }) => id),
      });
    },
    [navigate, officesSelected, order, page]
  );

  useEffect(() => {
    getOfficesAPI(ONLY_CONTRACT)
      .then((res) => {
        const offices = res.data ?? [];
        setOffices(res.data);

        if (savedState) {
          const savedOffices = offices.filter(
            ({ id }) => savedState?.officeIds?.includes(id) ?? false
          );
          setOfficesSelected(savedOffices);
          setPage(savedState.page);
          setOrder(savedState.order);

          updateList(savedState.page, savedState.order, savedState.officeIds);
          return;
        }

        const cookieSavedOffices = offices.filter((d) =>
          Cookies.get(MULTI_OFFICE_ID)
            ?.split(",")
            .includes(d?.id.toString() ?? "-1")
        );
        if (cookieSavedOffices.length) {
          setOfficesSelected(cookieSavedOffices);
          updateList(
            0,
            {
              "offices.displayName": "asc",
            },
            cookieSavedOffices.map(({ id }) => id)
          );
        }
      })
      .catch((e) => console.log("===Error occurred while getting offices==="));
  }, [savedState, updateList]);

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {DriverWorkCyclesConstants.DriverWorkCycleAndShifts}
          </Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <AutoCompleteMultiple
                    label={DriverWorkCyclesConstants.OfficeName}
                    options={offices}
                    value={officesSelected}
                    forSearch
                    uniqueKey="id"
                    limit={100}
                    getOptionLabel={getOfficeOptionLabel}
                    onChange={handleChangeOffices}
                  />
                </Grid>
                <Grid item container justifyContent="flex-end" xs={6}>
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleClickSearch}
                  />
                  <CommonButton
                    btnType="ResetSearchCriteria"
                    sx={{ ml: 2 }}
                    onClickBtn={handleClickReset}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <DriverWorkCycleList
                driverWorkCycles={driverWorkCycles}
                pagingData={{
                  page,
                  total,
                  size: PAGE_SIZE,
                }}
                orderTypes={order}
                onChangePage={handleChangePage}
                onChangeSort={handleChangeSort}
                onClickEdit={handleClickEdit}
              />
              {emptyResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
